import React, { useCallback, useContext, useState } from 'react';

import { QBox, QPageLoader, QStack, QText } from '@qualio/ui-components';

import { IntegrationContext } from '../../context/integration';
import { useFormEnabled } from '../../hooks/useFormEnabled';
import { useIntegrations } from '../../hooks/useIntegrations';
import { IntegrationList } from '../../integrationList';

function chunk<T>(arr: T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(arr.length / size) }, (_v, i) => arr.slice(i * size, i * size + size));
}

export const Integration: React.FC = () => {
  const { isLoading, data: integrationsEnabled } = useIntegrations();
  const [selected, setSelected] = useState<string | null>(null);
  const context = useContext(IntegrationContext);
  const { enableForm } = useFormEnabled();

  const onClick = useCallback(
    (arg) => () => {
      enableForm(() => (integrationsEnabled ?? []).map((item) => item.integration).includes(arg.source));
      setSelected(`${arg.source}-${arg.namespace}`);
    },
    [integrationsEnabled, enableForm],
  );

  const filterItem = new Set(context?.sourceFilter.map((item) => `${item.source}-${item.namespace}`));
  const filterIntegrationList = IntegrationList.filter((item) => filterItem.has(`${item.source}-${item.namespace}`));

  if (isLoading) {
    return <QPageLoader />;
  }

  return (
    <QStack direction="column" gap={3}>
      {chunk(filterIntegrationList, 4).map((items, rowIndex) => (
        <QStack direction="row" gap={3} key={`row-${rowIndex}`}>
          {items.map((item, columnIndex) => (
            <QBox
              width="185px"
              p="2"
              borderColor={selected === `${item.source}-${item.namespace}` ? 'blue.200' : 'gray.200'}
              borderWidth="1px"
              borderRadius="4px"
              alignSelf="center"
              display="flex"
              backgroundColor={selected === `${item.source}-${item.namespace}` ? 'blue.100' : 'white'}
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              _hover={{
                background: selected === `${item.source}-${item.namespace}` ? 'blue.200' : 'gray.200',
                cursor: 'pointer',
              }}
              rowGap={2}
              key={`item-${rowIndex}-${columnIndex}`}
              onClick={onClick(item)}
              data-cy="integration"
              data-integration={`integration-${item.source}-${item.namespace}`}
            >
              <img src={item.logo} width="100px" alt={item.label} />
              <QText>{item.label}</QText>
            </QBox>
          ))}
        </QStack>
      ))}
    </QStack>
  );
};
